/**
 * ProviderTable Component (T113 - US5)
 * Displays paginated list of active providers with actions
 * 
 * IMPORTANT: Displays total count reflecting applied filters
 * as per user requirement
 * 
 * Features:
 * - Sticky header that remains visible during scroll
 * - Sticky footer showing total count
 * - Internal scrolling (max-height with overflow)
 */

import { Edit2, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import type { AdminProvider } from '../types/admin-provider.type';
import { ProviderTableSkeleton } from './ProviderTableSkeleton';
import { ReferralLinkField } from './ReferralLinkField';
import { ProviderVettingStatusCell } from '@/features/vetting/components/ProviderVettingStatusCell';
import { useProviderVettingStatusBatch } from '@/features/vetting/hooks/useProviderVettingStatus';
type ProviderTableProps = {
  providers: AdminProvider[];
  totalCount: number;
  isFiltered?: boolean;
  onEdit: (providerId: string) => void;
  onDelete: (providerId: string) => void;
  isLoading?: boolean;
  maxHeight?: string;
};

/**
 * Provider table component displaying provider list with actions
 * 
 * @param providers - Array of providers to display
 * @param totalCount - Total number of providers (reflects filters if applied)
 * @param isFiltered - Whether filters are applied
 * @param onEdit - Callback when edit button is clicked
 * @param onDelete - Callback when delete button is clicked
 * @param isLoading - Whether data is loading
 * 
 * Features:
 * - Displays company name, location, vetting status, consultations, joined date
 * - Shows total count (filtered or unfiltered)
 * - Edit and delete actions per row
 * - Status badges with color coding
 * - Formatted dates
 * - Empty state message
 */
export function ProviderTable({
  providers,
  totalCount,
  isFiltered = false,
  onEdit,
  onDelete,
  isLoading = false,
  maxHeight = 'calc(100vh - 250px)',
}: ProviderTableProps) {
  // One batched request for every row's background-check status, instead of
  // one request per row (was flooding the network tab / hitting connection
  // limits at 300+ providers). Called unconditionally, before the early
  // returns below, to satisfy rules of hooks.
  const providerIds = providers.map((p) => p.id);
  // On a failed batch the cells show "—" (no summary), never a stuck loader.
  const { summaries: vettingStatusByProvider } = useProviderVettingStatusBatch(providerIds);

  // Show loading skeleton
  if (isLoading) {
    return <div data-testid="provider-table-skeleton"><ProviderTableSkeleton /></div>;
  }

  // Empty state
  if (providers.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="text-gray-400 mb-2" data-testid="empty-state-illustration">
          <svg
            className="h-12 w-12 mx-auto"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            data-testid="empty-state-icon"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
            />
          </svg>
        </div>
        {isFiltered ? (
          <>
            <p className="text-gray-500 font-medium">No providers match your filters</p>
            <p className="text-gray-400 text-sm mt-1">
              Try adjusting your filters or search terms
            </p>
          </>
        ) : (
          <>
            <p className="text-gray-500 font-medium">No providers found</p>
            <p className="text-gray-400 text-sm mt-1">
              Providers will appear here once registered
            </p>
          </>
        )}
      </div>
    );
  }

  // Helper to get status badge color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return 'bg-green-100 text-green-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'IN_REVIEW':
        return 'bg-blue-100 text-blue-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="flex flex-col border border-gray-200 rounded-md bg-white">
      {/* Sticky Header - Total count display */}
      <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
        <div className="text-sm">
          {isFiltered ? (
            <span>
              Showing{' '}
              <span className="font-semibold text-gray-900">
                {providers.length.toLocaleString()}
              </span>
              {' '}of{' '}
              <span className="font-semibold text-gray-900">
                {totalCount.toLocaleString()}
              </span>
              {' '}providers{' '}
              <span className="text-[#026857] font-medium">(Filtered)</span>
            </span>
          ) : (
            <span>
              Total{' '}
              <span className="font-semibold text-gray-900">
                {totalCount.toLocaleString()}
              </span>
              {' '}
              {totalCount === 1 ? 'provider' : 'providers'}
            </span>
          )}
        </div>
      </div>

      {/* Scrollable table container */}
      <div 
        className="overflow-auto"
        style={{ maxHeight }}
      >
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Company Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contact
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Location
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Background Check
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Consultations
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                New Clients
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Joined
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 shadow-[-2px_0_4px_rgba(0,0,0,0.05)]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {providers.map((provider) => {
              // Compute display name from company or user name
              const displayName = provider.companyName || 
                `${provider.user.firstName} ${provider.user.lastName || ''}`.trim();
              
              return (
                <tr
                  key={provider.id}
                  className="hover:bg-gray-50 transition-colors"
                >
                  {/* Company Name */}
                  <td className="px-4 py-4 align-top">
                    <div className="flex max-w-sm flex-col gap-1">
                      <div className="text-sm font-medium text-gray-900">
                        {displayName}
                      </div>
                      <ReferralLinkField
                        referralLink={provider.referralLink}
                        fallbackText="No referral link available"
                        copyLabel={`Copy referral link for ${displayName}`}
                        compact
                      />
                    </div>
                  </td>

                  {/* Contact */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{provider.user.email}</div>
                    <div className="text-xs text-gray-500">
                      {provider.user.firstName} {provider.user.lastName}
                    </div>
                  </td>

                  {/* Location */}
                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-900">
                      {provider.businessAddress || 'Not provided'}
                    </div>
                  </td>

                  {/* Status */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                        provider.vettingStatus,
                      )}`}
                    >
                      {provider.vettingStatus.replace('_', ' ')}
                    </span>
                  </td>

                  {/* Background Check (Feature 099 - Checkr integration) */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <ProviderVettingStatusCell
                      providerId={provider.id}
                      summary={vettingStatusByProvider[provider.id]}
                      fromBatch
                    />
                  </td>

                  {/* Consultations Count */}
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {provider.consultationCount ?? 0}
                  </td>

                  {/* New Client Acceptance */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        provider.newClientAcceptance
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {provider.newClientAcceptance ? 'Yes' : 'No'}
                    </span>
                  </td>

                  {/* Joined Date */}
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {format(new Date(provider.createdAt), 'MMM d, yyyy')}
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium sticky right-0 bg-white shadow-[-2px_0_4px_rgba(0,0,0,0.05)]">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => onEdit(provider.id)}
                        className="p-1.5 text-gray-600 hover:text-[#026857] hover:bg-[#026857]/10 rounded-md transition-colors"
                        aria-label={`Edit ${displayName}`}
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(provider.id)}
                        className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        aria-label={`Delete ${displayName}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Sticky Footer - Summary info */}
      <div className="sticky bottom-0 z-10 flex items-center justify-between px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-600">
        <div>
          {providers.length > 0 && (
            <span>
              Displaying {providers.length} {providers.length === 1 ? 'row' : 'rows'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-4">
          <span>Scroll for more →</span>
        </div>
      </div>
    </div>
  );
}
